import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  CheckCircle,
  Star,
  ExpandMore
} from '@mui/icons-material';

// Dark theme colors
const colors = {
  bgPrimary: '#0a0a0a',
  bgSecondary: '#141414',
  bgSurface: '#1a1a1a',
  border: '#2a2a2a',
  textPrimary: '#e5e5e5',
  textSecondary: '#a3a3a3',
  textMuted: '#737373',
  accentPrimary: '#3b82f6',
  accentSecondary: '#8b5cf6',
  success: '#22c55e'
};

const PricingPage = () => {
  const navigate = useNavigate();
  const [isAnnual, setIsAnnual] = useState(false);

  const plans = [
    {
      name: 'Free',
      description: 'Perfect for small teams getting started',
      price: { monthly: 0, annual: 0 },
      popular: false,
      features: [
        'Up to 5 team members',
        'Unlimited planning sessions',
        'Basic AI estimation suggestions',
        'Standard card decks (Fibonacci, Linear)',
        'Real-time collaboration',
        'Email support'
      ],
      limitations: [],
      cta: 'Get Started Free',
      ctaVariant: 'outlined'
    },
    {
      name: 'Pro',
      description: 'Advanced features for growing teams',
      price: { monthly: 12, annual: 9.60 },
      popular: true,
      features: [
        'Everything in Free, plus:',
        'Unlimited team members',
        'Advanced AI estimation with confidence scoring',
        'JIRA integration (import & sync)',
        'Team analytics & velocity tracking',
        'Sprint capacity forecasting',
        'Custom card decks & templates',
        'Priority support & chat',
        'Advanced reporting & exports'
      ],
      limitations: [],
      cta: 'Start Free Trial',
      ctaVariant: 'contained'
    },
    {
      name: 'Enterprise',
      description: 'Custom solutions for large organizations',
      price: { monthly: 'Custom', annual: 'Custom' },
      popular: false,
      features: [
        'Everything in Pro, plus:',
        'SAML SSO & advanced security',
        'Organization management & provisioning',
        'Compliance & audit reports',
        'Executive dashboards & insights',
        'Dedicated success manager',
        '24/7 phone & email support',
        'Custom integrations & API access',
        'On-premise deployment options'
      ],
      limitations: [],
      cta: 'Contact Sales',
      ctaVariant: 'outlined'
    }
  ];

  const comparisonFeatures = [
    {
      category: 'Core Planning',
      features: [
        { name: 'Team members', free: 'Up to 5', pro: 'Unlimited', enterprise: 'Unlimited' },
        { name: 'Planning sessions', free: 'Unlimited', pro: 'Unlimited', enterprise: 'Unlimited' },
        { name: 'Real-time collaboration', free: '✓', pro: '✓', enterprise: '✓' },
        { name: 'Mobile optimization', free: '✓', pro: '✓', enterprise: '✓' }
      ]
    },
    {
      category: 'AI & Estimation',
      features: [
        { name: 'Basic AI suggestions', free: '✓', pro: '✓', enterprise: '✓' },
        { name: 'Advanced AI with confidence scores', free: '-', pro: '✓', enterprise: '✓' },
        { name: 'Pattern recognition & insights', free: '-', pro: '✓', enterprise: '✓' }
      ]
    },
    {
      category: 'Integrations',
      features: [
        { name: 'JIRA integration', free: '-', pro: '✓', enterprise: '✓' },
        { name: 'REST API access', free: '-', pro: '✓', enterprise: '✓' },
        { name: 'Custom integrations', free: '-', pro: '-', enterprise: '✓' }
      ]
    },
    {
      category: 'Analytics & Reporting',
      features: [
        { name: 'Basic session reports', free: '✓', pro: '✓', enterprise: '✓' },
        { name: 'Team velocity & analytics', free: '-', pro: '✓', enterprise: '✓' },
        { name: 'Executive dashboards', free: '-', pro: '-', enterprise: '✓' },
        { name: 'Compliance reports', free: '-', pro: '-', enterprise: '✓' }
      ]
    },
    {
      category: 'Security & Enterprise',
      features: [
        { name: 'SSO (SAML)', free: '-', pro: '-', enterprise: '✓' },
        { name: 'Role-based access control', free: '-', pro: '-', enterprise: '✓' },
        { name: 'Audit trails', free: '-', pro: '-', enterprise: '✓' }
      ]
    },
    {
      category: 'Support',
      features: [
        { name: 'Email support', free: '✓', pro: '✓', enterprise: '✓' },
        { name: 'Priority chat support', free: '-', pro: '✓', enterprise: '✓' },
        { name: '24/7 phone support', free: '-', pro: '-', enterprise: '✓' },
        { name: 'Dedicated success manager', free: '-', pro: '-', enterprise: '✓' }
      ]
    }
  ];

  const faqs = [
    {
      question: 'Is there really a free plan forever?',
      answer: 'Yes! Our free plan includes all core planning poker features for up to 5 team members. You can run unlimited sessions and even get basic AI estimation suggestions.'
    },
    {
      question: 'How does the 14-day free trial work?',
      answer: 'Start your Pro trial instantly - no credit card required. You\'ll get full access to all Pro features. After 14 days, you can choose to upgrade or continue with our free plan.'
    },
    {
      question: 'Can I change plans anytime?',
      answer: 'Absolutely! You can upgrade, downgrade, or cancel your subscription at any time. Changes take effect at your next billing cycle.'
    },
    {
      question: 'What counts as a team member?',
      answer: 'A team member is anyone who participates in planning sessions. Session observers or viewers don\'t count towards your team member limit.'
    },
    {
      question: 'Do you offer annual discounts?',
      answer: 'Yes! Save 20% when you pay annually. For example, Pro plans cost $9.60/user/month when billed annually instead of $12/month.'
    },
    {
      question: 'What\'s included in Enterprise pricing?',
      answer: 'Enterprise pricing is custom based on your needs. It includes volume discounts, dedicated support, custom integrations, and on-premise deployment options.'
    }
  ];

  const getPrice = (plan) => {
    if (typeof plan.price.monthly === 'string') return plan.price.monthly;
    const price = isAnnual ? plan.price.annual : plan.price.monthly;
    return price === 0 ? 'Free' : `$${price}`;
  };

  const getSavings = (plan) => {
    if (typeof plan.price.monthly === 'string' || !isAnnual || plan.price.monthly === 0) return null;
    const savings = (plan.price.monthly - plan.price.annual) * 12;
    return `Save $${savings.toFixed(2)} per user/year`;
  };

  return (
    <Box sx={{ bgcolor: colors.bgPrimary, minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${colors.bgPrimary} 0%, ${colors.bgSecondary} 100%)`,
          py: { xs: 8, md: 10 },
          textAlign: 'center',
          borderBottom: `1px solid ${colors.border}`
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h1"
            component="h1"
            sx={{
              fontSize: { xs: '2.5rem', md: '3rem' },
              fontWeight: 700,
              mb: 3,
              background: `linear-gradient(135deg, ${colors.accentPrimary} 0%, ${colors.accentSecondary} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Simple, Transparent Pricing
          </Typography>

          <Typography variant="h5" sx={{ mb: 4, color: colors.textSecondary }}>
            Start free and scale as you grow. No hidden fees.
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 4 }}>
            <Typography variant="body1" sx={{ color: colors.textPrimary }}>Monthly</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={isAnnual}
                  onChange={(e) => setIsAnnual(e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: colors.accentPrimary,
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: colors.accentPrimary,
                    },
                  }}
                />
              }
              label=""
            />
            <Typography variant="body1" sx={{ color: colors.textPrimary }}>
              Annual
              <Chip
                label="Save 20%"
                size="small"
                sx={{ ml: 1, fontWeight: 'bold', bgcolor: colors.success, color: '#fff' }}
              />
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Pricing Plans */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Grid container spacing={4} justifyContent="center">
          {plans.map((plan, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  bgcolor: colors.bgSurface,
                  border: plan.popular ? `2px solid ${colors.accentPrimary}` : `1px solid ${colors.border}`,
                  transform: plan.popular ? 'scale(1.05)' : 'none',
                  '&:hover': {
                    transform: plan.popular ? 'scale(1.05) translateY(-4px)' : 'translateY(-4px)',
                    borderColor: colors.accentPrimary
                  }
                }}
              >
                {plan.popular && (
                  <Chip
                    icon={<Star />}
                    label="Most Popular"
                    sx={{
                      position: 'absolute',
                      top: -12,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      fontWeight: 'bold',
                      bgcolor: colors.accentPrimary,
                      color: '#fff'
                    }}
                  />
                )}

                <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography variant="h4" component="h3" gutterBottom fontWeight="bold" sx={{ color: colors.textPrimary }}>
                      {plan.name}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, color: colors.textSecondary }}>
                      {plan.description}
                    </Typography>

                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="h2"
                        component="div"
                        sx={{ fontWeight: 'bold', color: colors.textPrimary }}
                      >
                        {getPrice(plan)}
                        {typeof plan.price.monthly === 'number' && plan.price.monthly > 0 && (
                          <Typography component="span" variant="h6" sx={{ color: colors.textSecondary }}>
                            /user/month
                          </Typography>
                        )}
                        {plan.name === 'Free' && (
                          <Typography component="span" variant="h6" sx={{ color: colors.textSecondary }}>
                            forever
                          </Typography>
                        )}
                      </Typography>
                      {getSavings(plan) && (
                        <Typography variant="body2" sx={{ color: colors.success }} fontWeight="bold">
                          {getSavings(plan)}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ flexGrow: 1, mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ color: colors.textPrimary }}>
                      {plan.name === 'Free' ? "What's included:" : plan.name === 'Pro' ? "Everything in Free, plus:" : "Everything in Pro, plus:"}
                    </Typography>
                    <List dense>
                      {plan.features.map((feature, featureIndex) => (
                        <ListItem key={featureIndex} sx={{ px: 0, py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircle
                              fontSize="small"
                              sx={{ color: feature.includes('Everything in') ? colors.accentPrimary : colors.success }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={feature}
                            primaryTypographyProps={{
                              variant: 'body2',
                              fontWeight: feature.includes('Everything in') ? 'bold' : 'normal',
                              color: feature.includes('Everything in') ? colors.accentPrimary : colors.textPrimary
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>

                  <Box sx={{ textAlign: 'center' }}>
                    <Button
                      variant={plan.ctaVariant}
                      size="large"
                      fullWidth
                      onClick={() => {
                        if (plan.name === 'Enterprise') {
                          navigate('/contact');
                        } else {
                          navigate('/register');
                        }
                      }}
                      sx={{
                        py: 1.5,
                        mb: 2,
                        ...(plan.ctaVariant === 'contained' && {
                          background: `linear-gradient(135deg, ${colors.accentPrimary} 0%, ${colors.accentSecondary} 100%)`
                        }),
                        ...(plan.ctaVariant === 'outlined' && {
                          borderColor: colors.border,
                          color: colors.textPrimary,
                          '&:hover': {
                            borderColor: colors.accentPrimary,
                            bgcolor: 'rgba(59,130,246,0.1)'
                          }
                        })
                      }}
                    >
                      {plan.cta}
                    </Button>
                    <Typography variant="body2" sx={{ color: colors.textMuted }}>
                      {plan.name === 'Free' ? 'No credit card required' :
                       plan.name === 'Pro' ? '14-day free trial' :
                       'Custom onboarding included'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Feature Comparison */}
      <Box sx={{ bgcolor: colors.bgSecondary, py: { xs: 8, md: 12 }, borderTop: `1px solid ${colors.border}`, borderBottom: `1px solid ${colors.border}` }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Typography variant="h2" component="h2" gutterBottom fontWeight="bold" sx={{ color: colors.textPrimary }}>
              Compare All Features
            </Typography>
            <Typography variant="h5" sx={{ color: colors.textSecondary }}>
              See exactly what's included in each plan
            </Typography>
          </Box>

          <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto', bgcolor: colors.bgSurface, border: `1px solid ${colors.border}` }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem', color: colors.textPrimary, borderColor: colors.border }}>Features</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem', color: colors.textPrimary, borderColor: colors.border }}>Free</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem', color: colors.textPrimary, bgcolor: 'rgba(59,130,246,0.1)', borderColor: colors.border }}>Pro</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem', color: colors.textPrimary, borderColor: colors.border }}>Enterprise</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {comparisonFeatures.map((category) => (
                  <React.Fragment key={category.category}>
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        sx={{
                          bgcolor: colors.bgSecondary,
                          fontWeight: 'bold',
                          fontSize: '0.875rem',
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          color: colors.textSecondary,
                          borderColor: colors.border
                        }}
                      >
                        {category.category}
                      </TableCell>
                    </TableRow>
                    {category.features.map((feature, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ fontWeight: 500, color: colors.textPrimary, borderColor: colors.border }}>{feature.name}</TableCell>
                        <TableCell align="center" sx={{ color: colors.textPrimary, borderColor: colors.border }}>{feature.free}</TableCell>
                        <TableCell align="center" sx={{ bgcolor: 'rgba(59,130,246,0.05)', color: colors.textPrimary, borderColor: colors.border }}>{feature.pro}</TableCell>
                        <TableCell align="center" sx={{ color: colors.textPrimary, borderColor: colors.border }}>{feature.enterprise}</TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Container maxWidth="md" sx={{ py: { xs: 8, md: 12 } }}>
        <Box textAlign="center" mb={6}>
          <Typography variant="h2" component="h2" gutterBottom fontWeight="bold" sx={{ color: colors.textPrimary }}>
            Frequently Asked Questions
          </Typography>
          <Typography variant="h5" sx={{ color: colors.textSecondary }}>
            Everything you need to know about pricing
          </Typography>
        </Box>

        {faqs.map((faq, index) => (
          <Accordion
            key={index}
            sx={{
              mb: 2,
              bgcolor: colors.bgSurface,
              border: `1px solid ${colors.border}`,
              '&:before': { display: 'none' },
              '&.Mui-expanded': { margin: '0 0 16px 0' }
            }}
            elevation={0}
          >
            <AccordionSummary expandIcon={<ExpandMore sx={{ color: colors.textSecondary }} />}>
              <Typography variant="h6" fontWeight="bold" sx={{ color: colors.textPrimary }}>
                {faq.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body1" sx={{ color: colors.textSecondary }} lineHeight={1.7}>
                {faq.answer}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Container>

      {/* CTA Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${colors.accentPrimary} 0%, ${colors.accentSecondary} 100%)`,
          color: 'white',
          py: { xs: 8, md: 12 },
          textAlign: 'center'
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h2" component="h2" gutterBottom fontWeight="bold">
            Start Planning Today
          </Typography>

          <Typography variant="h5" sx={{ mb: 4, opacity: 0.9 }}>
            Free plan available. No credit card required.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/register')}
              sx={{
                px: 4,
                py: 1.5,
                bgcolor: 'white',
                color: colors.accentPrimary,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
              }}
            >
              Get Started Free
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/contact')}
              sx={{
                px: 4,
                py: 1.5,
                borderColor: 'rgba(255,255,255,0.3)',
                color: 'white',
                '&:hover': {
                  borderColor: 'rgba(255,255,255,0.5)',
                  bgcolor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              Contact Sales
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default PricingPage;
